import {Box} from '@dropbox/dig-foundations';
import {autoUpdate, computePosition, flip, offset, shift, useFloating} from '@floating-ui/react';
import {$isListItemNode, ListItemNode} from '@lexical/list';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {createDOMRange} from '@lexical/selection';
import {
  $getTableColumnIndexFromTableCellNode,
  $getTableRowIndexFromTableCellNode,
  $isTableCellNode,
  $isTableSelection,
  TableSelection,
} from '@lexical/table';
import {$findMatchingParent, mergeRegister} from '@lexical/utils';
import {activeAISuggestionAtom} from 'atoms/aiSuggestion';
import {activeCommentAtom, type CommentState} from 'atoms/comment';
import {useCollabContext} from 'components/DSYS/CollabProvider';
import {isEphemeralHighlightClassToken} from 'components/DSYS/editor/nodes/HighlightNode';
import commentStyles from 'components/DSYS/editor/plugins/Comment.module.css';
import {
  clearLiveEditorSelectionForCommentMode,
  installEditorFocusGuard,
} from 'components/DSYS/editor/plugins/comments/commentFocus';
import {useThreads} from 'components/DSYS/editor/plugins/comments/useThreads';
import mentionStyles from 'components/DSYS/editor/plugins/Mention.module.css';
import {isInsideNestedEditor} from 'components/DSYS/editor/utils';
import {
  registerCommentEditorRoot,
  unregisterCommentEditorRoot,
} from 'components/DSYS/editor/utils/commentEditorRoots';
import {
  getMostSpecificActiveHighlightId,
  setThreadsDataToEditorRoot,
  unregisterThreadsDataFromEditorRoot,
} from 'components/DSYS/editor/utils/highlightHelpers';
import {getPlanningCommentOriginIdFromElement} from 'components/DSYS/editor/utils/planningCommentOrigin';
import {readStickyOffsetTopPx} from 'components/DSYS/tableStickyLayout';
import {shouldLetOutsideCommentClickThrough} from 'helpers/commentDraft';
import {useCommentActions} from 'hooks/useCommentActions';
import {useAtom, useAtomValue} from 'jotai';
import {
  $getNearestNodeFromDOMNode,
  $getSelection,
  $isRangeSelection,
  $setSelection,
  COMMAND_PRIORITY_HIGH,
  KEY_ESCAPE_COMMAND,
  LexicalNode,
  SKIP_COLLAB_TAG,
  SKIP_DOM_SELECTION_TAG,
} from 'lexical';
import {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';

import {createDefaultClickHandlers, NodeClickHandler} from './clickHandlers';
import {
  clampCommentDisplayLeft,
  clampCommentDisplayTopToBoundary,
  COMMENT_DISPLAY_VIEWPORT_PADDING_PX,
  COMMENT_DISPLAY_WIDTH_PX,
  getCommentDisplayHorizontalBounds,
  getCommentDisplayTopBoundary,
} from './commentDisplaySizing';
import {MentionTypeaheadOption} from './MentionPlugin';
import {SelectedTextControls, SelectedTextToolbar} from './SelectedTextToolbar';
import {shouldHideToolbarForCollapsedCaretScroll} from './textSelectionScroll';

// Thread IDs that are currently being created
export const pendingThreadIds = new Set<string>();

type ThreadRenderStatus = {
  isResolved?: boolean | null;
  hasActiveComments?: boolean;
};

export const syncCommentHighlightElementClasses = ({
  element,
  activeThreadId,
  hoveredThreadId,
  pendingIds,
  threadStatus,
}: {
  element: HTMLElement;
  activeThreadId?: string;
  hoveredThreadId?: string;
  pendingIds: ReadonlySet<string>;
  threadStatus: ReadonlyMap<string, ThreadRenderStatus>;
}) => {
  const highlightIdAttr = element.getAttribute('data-highlight-id');
  if (!highlightIdAttr) return;

  Array.from(element.classList)
    .filter((classToken) => isEphemeralHighlightClassToken(classToken))
    .forEach((classToken) => element.classList.remove(classToken));

  const elementThreadIds = highlightIdAttr.split(' ').filter(Boolean);
  const isActiveThread = activeThreadId && elementThreadIds.includes(activeThreadId);
  const isHoveredThread = hoveredThreadId && elementThreadIds.includes(hoveredThreadId);
  const isPendingThread = elementThreadIds.some((id) => pendingIds.has(id));

  const hasUnresolvedActiveThread = elementThreadIds.some((id) => {
    const status = threadStatus.get(id);
    return status ? !status.isResolved && status.hasActiveComments : false;
  });

  const shouldShowComment = hasUnresolvedActiveThread || isPendingThread || Boolean(isActiveThread);

  if (shouldShowComment) {
    element.classList.add(commentStyles.comment);
  }

  if (isActiveThread) {
    element.classList.add(commentStyles.focused);
  }

  if (shouldShowComment && isHoveredThread && !isActiveThread) {
    element.classList.add(commentStyles.highlighted);
  }
};

type TextSelectionPluginProps = {
  controls: SelectedTextControls[];
  alwaysShow?: boolean;
  documentSynced?: boolean;
  hideCommentHighlights?: boolean;
  hoveredThreadId?: string;
  commentMentions?: MentionTypeaheadOption[];
  suppressActiveCommentDisplay?: boolean;
  disableAISuggestions?: boolean;
};

export const shouldRenderFloatingCommentShell = ({
  activeComment,
  isOwningEditor,
  commentVirtualElement,
}: {
  activeComment: {threadId: string} | null;
  isOwningEditor: boolean;
  commentVirtualElement: {getBoundingClientRect(): DOMRect} | null;
}) => {
  if (!activeComment) {
    return true;
  }

  return isOwningEditor && Boolean(commentVirtualElement);
};

export const hasResolvedFloatingPosition = ({
  isPositioned,
  x,
  y,
}: {
  isPositioned: boolean;
  x: number | null | undefined;
  y: number | null | undefined;
}) => isPositioned && x != null && y != null;

export const shouldAutoFocusActiveCommentInput = ({
  activeComment,
  isWaitingForActiveCommentPosition,
  useSourceAwareFocus,
}: {
  activeComment: Pick<CommentState, 'source'> | null;
  isWaitingForActiveCommentPosition: boolean;
  useSourceAwareFocus: boolean;
}) => {
  if (isWaitingForActiveCommentPosition) {
    return false;
  }

  if (!useSourceAwareFocus || !activeComment) {
    return true;
  }

  return ['create', 'preview', 'drawer'].includes(activeComment.source ?? '');
};

export const shouldPreserveEditorSelectionForActiveComment = ({
  activeComment,
}: {
  activeComment: Pick<CommentState, 'source'> | null;
}) => activeComment?.source === 'highlight';

const isCommentPositionDebugEnabled = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  return (
    window.localStorage.getItem('debugCommentPosition') === '1' ||
    new URLSearchParams(window.location.search).has('debugCommentPosition')
  );
};

const debugCommentPosition = (event: string, payload: Record<string, unknown>) => {
  if (!isCommentPositionDebugEnabled()) {
    return;
  }

  console.debug(`[comment-position] ${event}`, payload);
};

const rectToDebugObject = (rect: DOMRect) => ({
  top: Math.round(rect.top),
  left: Math.round(rect.left),
  bottom: Math.round(rect.bottom),
  right: Math.round(rect.right),
  width: Math.round(rect.width),
  height: Math.round(rect.height),
});

// Virtual element interface for Floating UI
interface VirtualElement {
  getBoundingClientRect(): DOMRect;
  contextElement?: Element;
}

type ActiveCommentPosition = {
  threadId: string;
  x: number;
  y: number;
};

const MAX_COMMENT_ANCHOR_RETRY_FRAMES = 30;
const MOUSE_SELECTION_DRAG_THRESHOLD_PX = 4;
const DYNAMIC_NODE_FOCUS_BOUNDARY_SELECTOR = '[data-dynamic-node-focus-boundary="true"]';

export const isUsableCommentAnchorRect = (rect: DOMRect | null | undefined) =>
  Boolean(rect && rect.width > 0 && rect.height > 0);

export const hasOpenCommentBox = (): boolean =>
  document.querySelectorAll('[data-comment-box]').length > 0;

export const isInsideDynamicNodeFocusBoundary = ({
  editorElement,
  target,
}: {
  editorElement: HTMLElement;
  target: HTMLElement;
}): boolean => {
  const boundary = target.closest<HTMLElement>(DYNAMIC_NODE_FOCUS_BOUNDARY_SELECTOR);
  return Boolean(boundary && editorElement.contains(boundary));
};

export const shouldClearRootSelectionForDynamicNodeMouseDown = ({
  editorElement,
  target,
}: {
  editorElement: HTMLElement;
  target: HTMLElement;
}): boolean =>
  isInsideDynamicNodeFocusBoundary({editorElement, target}) &&
  !target.closest('[data-highlight-id]');

export const shouldCloseActiveCommentForDynamicNodeMouseDown = ({
  editorElement,
  event,
  target,
}: {
  editorElement: HTMLElement;
  event: MouseEvent;
  target: HTMLElement;
}): boolean =>
  shouldClearRootSelectionForDynamicNodeMouseDown({editorElement, target}) &&
  !shouldLetOutsideCommentClickThrough(event);

export const getFloatingReferenceRectFromRange = (range: Range): DOMRect | null => {
  try {
    const rect = range.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) {
      return null;
    }

    return new DOMRect(rect.left + rect.width / 2, rect.top, 1, rect.height);
  } catch {
    return null;
  }
};

const isPointInsideRect = (rect: DOMRect, x: number, y: number) =>
  x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;

export const doesRangeContainPoint = (
  range: Pick<Range, 'getBoundingClientRect' | 'getClientRects'>,
  x: number,
  y: number
) => {
  const clientRects = Array.from(range.getClientRects());
  if (clientRects.some((rect) => isPointInsideRect(rect, x, y))) {
    return true;
  }

  if (clientRects.length > 0) {
    return false;
  }

  const boundingRect = range.getBoundingClientRect();
  return boundingRect.width > 0 && boundingRect.height > 0 && isPointInsideRect(boundingRect, x, y);
};

export const shouldKeepToolbarVisibleOnMouseDown = ({
  editorElement,
  selection,
  target,
  clientX,
  clientY,
}: {
  editorElement: HTMLElement;
  selection: Selection | null;
  target: HTMLElement;
  clientX: number;
  clientY: number;
}) => {
  if (!selection || selection.rangeCount === 0 || selection.toString().trim() === '') {
    return false;
  }

  const range = selection.getRangeAt(0);
  if (
    !editorElement.contains(range.commonAncestorContainer) ||
    isInsideNestedEditor(range.commonAncestorContainer, editorElement) ||
    !editorElement.contains(target) ||
    isInsideNestedEditor(target, editorElement)
  ) {
    return false;
  }

  return doesRangeContainPoint(range, clientX, clientY);
};

export const shouldSuppressMouseUpToolbarRestore = ({
  hadTextSelectionOnMouseDown,
  didPointerMoveSinceMouseDown,
  clickDetail,
}: {
  hadTextSelectionOnMouseDown: boolean;
  didPointerMoveSinceMouseDown: boolean;
  clickDetail: number;
}) => hadTextSelectionOnMouseDown && !didPointerMoveSinceMouseDown && clickDetail <= 1;

/**
 * Calculate the combined bounding rectangle that encompasses all provided elements
 */
function calculateCombinedBoundingRect(elements: HTMLElement[]): DOMRect | null {
  if (elements.length === 0) return null;

  const rects = elements.map((el) => el.getBoundingClientRect());

  const left = Math.min(...rects.map((r) => r.left));
  const top = Math.min(...rects.map((r) => r.top));
  const right = Math.max(...rects.map((r) => r.right));
  const bottom = Math.max(...rects.map((r) => r.bottom));

  const width = right - left;
  const height = bottom - top;

  return new DOMRect(left, top, width, height);
}

/**
 * Create a virtual element for AI suggestion positioning that encompasses all nodes with the same suggestion ID
 */
function createVirtualElementForSuggestion(
  suggestionId: string,
  root: HTMLElement | null
): VirtualElement | null {
  const elements = Array.from(
    document.querySelectorAll(`[data-suggestion-id="${suggestionId}"]`)
  ) as HTMLElement[];

  const scoped = root
    ? elements.filter((el) => root.contains(el) && !isInsideNestedEditor(el, root))
    : elements;
  if (scoped.length === 0) return null;

  return {
    getBoundingClientRect(): DOMRect {
      const combinedRect = calculateCombinedBoundingRect(scoped);
      return combinedRect || new DOMRect(0, 0, 0, 0);
    },
  };
}

/**
 * Create a virtual element for comment positioning that encompasses all nodes with the same highlight ID
 */
function createVirtualElementForComment(
  threadId: string,
  root: HTMLElement | null
): VirtualElement | null {
  const elements = Array.from(
    document.querySelectorAll(`[data-highlight-id~="${threadId}"]`)
  ) as HTMLElement[];

  if (!elements.length) {
    return null;
  }

  const scoped = root
    ? elements.filter((el) => root.contains(el) && !isInsideNestedEditor(el, root))
    : elements;
  if (scoped.length === 0) {
    return null;
  }

  return {
    getBoundingClientRect(): DOMRect {
      const combinedRect = calculateCombinedBoundingRect(scoped);
      return combinedRect || new DOMRect(0, 0, 0, 0);
    },
  };
}

export const TextSelectionPlugin = ({
  controls,
  alwaysShow,
  documentSynced,
  hideCommentHighlights,
  hoveredThreadId,
  commentMentions,
  suppressActiveCommentDisplay = false,
  disableAISuggestions = false,
}: TextSelectionPluginProps) => {
  const [editor] = useLexicalComposerContext();
  const [layoutRect, setLayoutRect] = useState<DOMRect | null>(null);
  const [isToolbarVisible, setIsToolbarVisible] = useState(false);
  const [isSelectionActive, setIsSelectionActive] = useState(false);
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [tableSelection, setTableSelection] = useState<TableSelection | null>(null);
  const [isEditorFocused, setIsEditorFocused] = useState(false);
  const suppressCollapsedCaretToolbarRef = useRef(false);
  const collapsedCaretFocusGuardCleanupRef = useRef<(() => void) | null>(null);
  const mouseUpFrameRef = useRef<number | null>(null);
  const mouseSelectionGestureRef = useRef<{
    hadTextSelectionOnMouseDown: boolean;
    keepToolbarVisibleOnMouseDown: boolean;
    startX: number;
    startY: number;
    didPointerMoveSinceMouseDown: boolean;
  } | null>(null);

  // Track if a click handler processed the last click (used to avoid race conditions with async state)
  const clickHandlerProcessedRef = useRef(false);
  // Set on mousedown when the target will be handled by the click handler, so mouseup skips hideToolbar
  const pendingClickHandlerRef = useRef(false);
  // AI suggestion and comment state - use atoms with setters for full control
  const [activeAISuggestion, setActiveAISuggestion] = useAtom(activeAISuggestionAtom);
  const activeComment = useAtomValue(activeCommentAtom);
  const {planningId} = useCollabContext();
  const {openComment, closeComment} = useCommentActions();
  const setActiveComment = useCallback(
    (
      value: {threadId: string; editorKey?: string; planningCommentOriginId?: string | null} | null,
      source: 'url' | 'highlight' | 'programmatic' = 'highlight'
    ) => {
      if (value === null) {
        return closeComment();
      } else {
        return openComment({...value, source});
      }
    },
    [closeComment, openComment]
  );
  const activeCommentRef = useRef(activeComment);
  activeCommentRef.current = activeComment;
  const effectiveActiveAISuggestion = disableAISuggestions ? null : activeAISuggestion;

  useLayoutEffect(() => {
    if (disableAISuggestions) {
      setActiveAISuggestion(null);
    }
  }, [disableAISuggestions, setActiveAISuggestion]);

  // Get threads data for comment validation
  const {threads, isLoading: isThreadsLoading} = useThreads();

  useEffect(() => {
    if (!threads?.length || pendingThreadIds.size === 0) {
      return;
    }

    threads.forEach((thread) => {
      if (thread?.id) {
        pendingThreadIds.delete(thread.id);
      }
    });
  }, [threads]);

  // Register threads data with editor root for HighlightNode hover handlers
  useEffect(() => {
    const unregister = editor.registerRootListener((rootElement, prevRootElement) => {
      if (prevRootElement !== null) {
        unregisterThreadsDataFromEditorRoot(prevRootElement);
      }
      if (rootElement !== null) {
        setThreadsDataToEditorRoot(rootElement, threads);
      }
    });

    const rootElement = editor.getRootElement();
    if (rootElement) {
      setThreadsDataToEditorRoot(rootElement, threads);
    }

    return () => {
      unregister();
      const root = editor.getRootElement();
      if (root) {
        unregisterThreadsDataFromEditorRoot(root);
      }
    };
  }, [editor, threads]);

  useLayoutEffect(() => {
    const editorKey = editor.getKey();
    const unregister = editor.registerRootListener((rootElement, prevRootElement) => {
      if (prevRootElement !== null) {
        unregisterCommentEditorRoot(prevRootElement, editorKey);
      }
      if (rootElement !== null) {
        registerCommentEditorRoot(rootElement, editorKey);
      }
    });

    const rootElement = editor.getRootElement();
    if (rootElement) {
      registerCommentEditorRoot(rootElement, editorKey);
    }

    return () => {
      unregister();
      const root = editor.getRootElement();
      if (root) {
        unregisterCommentEditorRoot(root, editorKey);
      }
    };
  }, [editor]);

  // Create default click handlers
  const clickHandlers = useMemo(
    () =>
      createDefaultClickHandlers({
        setActiveAISuggestion,
        setActiveComment,
        threads,
        disableAISuggestions,
      }),
    [disableAISuggestions, setActiveAISuggestion, setActiveComment, threads]
  );

  // Track which thread IDs have highlights owned by this editor (not nested editors).
  // Built once on mount and kept current by the MutationObserver below.
  const ownedThreadIdsRef = useRef<Set<string>>(new Set());

  // Hide highlights for orphaned threads (thread no longer exists) and resolved threads
  // Use useLayoutEffect to run before paint, avoiding flash of highlight then removal
  const activeThreadId = activeComment?.threadId;
  useLayoutEffect(() => {
    const editorRoot = editor.getRootElement();

    // Don't run cleanup until threads have loaded - otherwise we'd remove all highlights
    if (isThreadsLoading || threads === undefined) {
      return;
    }

    const scanOwnedThreadIds = (): Set<string> => {
      const ids = new Set<string>();
      if (!editorRoot) return ids;
      editorRoot.querySelectorAll('span[data-highlight-id]').forEach((el) => {
        if (isInsideNestedEditor(el as HTMLElement, editorRoot)) return;
        const attr = el.getAttribute('data-highlight-id');
        if (attr) {
          attr.split(' ').forEach((id) => {
            if (id) ids.add(id);
          });
        }
      });
      return ids;
    };

    ownedThreadIdsRef.current = scanOwnedThreadIds();

    // Precompute thread status outside the toggle function since it only
    // changes when the effect dependencies change
    const threadStatus = new Map(
      threads?.map((thread) => {
        const hasActiveComments = thread.comments?.some((comment) => !comment.is_deleted);
        return [thread.id, {isResolved: thread.is_resolved, hasActiveComments}];
      })
    );

    const toggleHighlightedThreads = () => {
      if (!editorRoot) return;

      const owned = ownedThreadIdsRef.current;
      if (owned.size === 0) {
        if (hideCommentHighlights) {
          editorRoot.classList.remove('threadsLoaded');
        } else if (!editorRoot.classList.contains('threadsLoaded')) {
          editorRoot.classList.add('threadsLoaded');
        }
        return;
      }

      const allHighlightElements = editorRoot.querySelectorAll('span[data-highlight-id]');
      allHighlightElements.forEach((element) => {
        const htmlElement = element as HTMLElement;
        if (isInsideNestedEditor(htmlElement, editorRoot)) {
          return;
        }
        syncCommentHighlightElementClasses({
          element: htmlElement,
          activeThreadId,
          hoveredThreadId,
          pendingIds: pendingThreadIds,
          threadStatus,
        });
      });

      if (hideCommentHighlights) {
        editorRoot.classList.remove('threadsLoaded');
      } else if (!editorRoot.classList.contains('threadsLoaded')) {
        editorRoot.classList.add('threadsLoaded');
      }
    };

    toggleHighlightedThreads();

    let rafId: number | null = null;
    const mo = new MutationObserver(() => {
      ownedThreadIdsRef.current = scanOwnedThreadIds();
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        toggleHighlightedThreads();
      });
    });
    if (editorRoot) {
      mo.observe(editorRoot, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-highlight-id'],
      });
    }

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      mo.disconnect();
      editorRoot?.classList.remove('threadsLoaded');
    };
  }, [threads, activeThreadId, hoveredThreadId, editor, isThreadsLoading, hideCommentHighlights]);

  // Create virtual element for AI suggestion that encompasses all nodes with same suggestion ID
  const stableSuggestionElement = useMemo(() => ({getBoundingClientRect: () => new DOMRect()}), []);
  const suggestionVirtualElement = useMemo(
    () =>
      effectiveActiveAISuggestion?.suggestion?.id
        ? createVirtualElementForSuggestion(
            effectiveActiveAISuggestion.suggestion.id,
            editor.getRootElement()
          )
        : null,
    [effectiveActiveAISuggestion?.suggestion?.id, editor]
  );

  // Create virtual element for comment that encompasses all nodes with same highlight ID
  const [commentAnchorVersion, setCommentAnchorVersion] = useState(0);
  const stableCommentElement = useMemo(() => ({getBoundingClientRect: () => new DOMRect()}), []);
  // Only create virtual element if:
  // 1. Document is synced and there's an active comment
  // 2. Either no editorKey is specified (legacy) OR this editor matches the specified key
  const isOwningEditor = !activeComment?.editorKey || activeComment.editorKey === editor.getKey();
  const commentVirtualElement = useMemo(
    () =>
      documentSynced && activeComment?.threadId
        ? createVirtualElementForComment(activeComment.threadId, editor.getRootElement())
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [documentSynced, activeComment?.threadId, editor, commentAnchorVersion]
  );

  // Determine if we should use enhanced positioning for AI suggestions
  const floatingMiddleware = activeComment
    ? [offset(10), shift({padding: 16, mainAxis: false, crossAxis: true})]
    : [offset(10), flip({padding: 16}), shift({padding: 16})];
  const isFloatingOpen = Boolean(
    documentSynced &&
    (activeComment || effectiveActiveAISuggestion || isToolbarVisible || tableSelection)
  );

  const {x, y, refs, strategy, update, placement, isPositioned} = useFloating({
    placement: 'top',
    strategy: 'fixed',
    open: isFloatingOpen,
    whileElementsMounted: autoUpdate,
    middleware: floatingMiddleware,
  });
  const [activeCommentReferenceThreadId, setActiveCommentReferenceThreadId] = useState<
    string | null
  >(null);
  const [activeCommentPosition, setActiveCommentPosition] = useState<ActiveCommentPosition | null>(
    null
  );
  const [floatingElement, setFloatingElement] = useState<HTMLElement | null>(null);
  const [floatingReferenceRect, setFloatingReferenceRect] = useState<DOMRect | null>(null);
  const floatingReferenceRectProviderRef = useRef<(() => DOMRect | null) | null>(null);
  const activeCommentPositionRequestRef = useRef(0);
  const commentAnchorRetryRef = useRef<{
    threadId: string | null;
    frameId: number | null;
    attempts: number;
  }>({threadId: null, frameId: null, attempts: 0});
  const setFloatingRef = useCallback(
    (node: HTMLElement | null) => {
      refs.setFloating(node);
      setFloatingElement(node);
    },
    [refs]
  );

  const updateFloatingReferenceRectState = useCallback((rect: DOMRect | null) => {
    setFloatingReferenceRect((previousRect) => {
      if (!rect) {
        return previousRect ? null : previousRect;
      }

      if (
        previousRect &&
        previousRect.top === rect.top &&
        previousRect.bottom === rect.bottom &&
        previousRect.left === rect.left &&
        previousRect.right === rect.right &&
        previousRect.width === rect.width &&
        previousRect.height === rect.height
      ) {
        return previousRect;
      }

      return rect;
    });
  }, []);

  const setStableFloatingReferenceRect = useCallback(
    (rect: DOMRect | null, rectProvider?: () => DOMRect | null) => {
      floatingReferenceRectProviderRef.current = rectProvider ?? (rect ? () => rect : null);
      updateFloatingReferenceRectState(rect);
    },
    [updateFloatingReferenceRectState]
  );

  const syncFloatingReferenceRect = useCallback(() => {
    const rect = floatingReferenceRectProviderRef.current?.() ?? null;
    updateFloatingReferenceRectState(rect);
  }, [updateFloatingReferenceRectState]);

  const clearActiveCommentAnchorRetry = useCallback(() => {
    const retry = commentAnchorRetryRef.current;
    if (retry.frameId !== null) {
      cancelAnimationFrame(retry.frameId);
    }
    commentAnchorRetryRef.current = {threadId: null, frameId: null, attempts: 0};
  }, []);

  const scheduleActiveCommentAnchorRetry = useCallback(
    (threadId: string, source: string) => {
      const retry = commentAnchorRetryRef.current;
      if (retry.threadId !== threadId) {
        if (retry.frameId !== null) {
          cancelAnimationFrame(retry.frameId);
        }
        retry.threadId = threadId;
        retry.frameId = null;
        retry.attempts = 0;
      }

      if (retry.frameId !== null || retry.attempts >= MAX_COMMENT_ANCHOR_RETRY_FRAMES) {
        return;
      }

      retry.attempts += 1;
      retry.frameId = requestAnimationFrame(() => {
        retry.frameId = null;
        if (activeCommentRef.current?.threadId !== threadId) {
          return;
        }

        debugCommentPosition('anchor-retry', {
          source,
          threadId,
          attempt: retry.attempts,
          editorKey: editor.getKey(),
        });
        setCommentAnchorVersion((version) => version + 1);
      });
    },
    [editor]
  );

  const computeActiveCommentPosition = useCallback(
    (source: string, threadId = activeComment?.threadId) => {
      if (!threadId || activeCommentRef.current?.threadId !== threadId || !floatingElement) {
        return;
      }

      const referenceRect = stableCommentElement.getBoundingClientRect();
      if (!isUsableCommentAnchorRect(referenceRect)) {
        activeCommentPositionRequestRef.current += 1;
        setActiveCommentPosition(null);
        setActiveCommentReferenceThreadId(null);
        scheduleActiveCommentAnchorRetry(threadId, source);
        debugCommentPosition('position-waiting-for-anchor-layout', {
          source,
          threadId,
          editorKey: editor.getKey(),
          referenceRect: rectToDebugObject(referenceRect),
        });
        return;
      }

      const requestId = activeCommentPositionRequestRef.current + 1;
      activeCommentPositionRequestRef.current = requestId;

      computePosition(stableCommentElement, floatingElement, {
        placement: 'top',
        strategy: 'fixed',
        middleware: [offset(10), shift({padding: 16, mainAxis: false, crossAxis: true})],
      }).then((position) => {
        if (
          activeCommentPositionRequestRef.current !== requestId ||
          activeCommentRef.current?.threadId !== threadId
        ) {
          return;
        }

        const horizontalBounds = getCommentDisplayHorizontalBounds(editor.getRootElement());
        const floatingWidth =
          floatingElement.getBoundingClientRect().width || COMMENT_DISPLAY_WIDTH_PX;
        const clampedX = clampCommentDisplayLeft(position.x, floatingWidth, horizontalBounds);
        const topBoundary = getCommentDisplayTopBoundary(
          readStickyOffsetTopPx(),
          COMMENT_DISPLAY_VIEWPORT_PADDING_PX
        );
        const clampedY = clampCommentDisplayTopToBoundary(position.y, topBoundary);

        setActiveCommentPosition({threadId, x: clampedX, y: clampedY});
        debugCommentPosition('position-ready', {
          source,
          threadId,
          editorKey: editor.getKey(),
          floating: {
            x: Math.round(clampedX),
            rawX: Math.round(position.x),
            y: Math.round(clampedY),
            rawY: Math.round(position.y),
            placement: position.placement,
          },
        });
      });
    },
    [
      activeComment?.threadId,
      editor,
      floatingElement,
      scheduleActiveCommentAnchorRetry,
      stableCommentElement,
    ]
  );

  const applyActiveCommentReference = useCallback(
    (source: string) => {
      if (!activeComment?.threadId || !commentVirtualElement || !isOwningEditor) {
        return false;
      }

      const referenceRect = commentVirtualElement.getBoundingClientRect();
      if (!isUsableCommentAnchorRect(referenceRect)) {
        activeCommentPositionRequestRef.current += 1;
        setActiveCommentReferenceThreadId(null);
        setActiveCommentPosition(null);
        setIsToolbarVisible(true);
        scheduleActiveCommentAnchorRetry(activeComment.threadId, source);
        debugCommentPosition('reference-waiting-for-anchor-layout', {
          source,
          threadId: activeComment.threadId,
          editorKey: editor.getKey(),
          referenceRect: rectToDebugObject(referenceRect),
        });
        return true;
      }

      clearActiveCommentAnchorRetry();
      stableCommentElement.getBoundingClientRect = commentVirtualElement.getBoundingClientRect;
      refs.setReference(stableCommentElement);
      setStableFloatingReferenceRect(commentVirtualElement.getBoundingClientRect());
      setActiveCommentReferenceThreadId(activeComment.threadId);
      setIsToolbarVisible(true);
      update();
      const threadId = activeComment.threadId;
      requestAnimationFrame(() => {
        computeActiveCommentPosition(source, threadId);
      });

      debugCommentPosition('reference-applied', {
        source,
        threadId: activeComment.threadId,
        editorKey: editor.getKey(),
        referenceRect: rectToDebugObject(referenceRect),
        floating: {x, y, isPositioned},
      });

      return true;
    },
    [
      activeComment?.threadId,
      clearActiveCommentAnchorRetry,
      commentVirtualElement,
      editor,
      computeActiveCommentPosition,
      isOwningEditor,
      isPositioned,
      refs,
      setStableFloatingReferenceRect,
      stableCommentElement,
      scheduleActiveCommentAnchorRetry,
      update,
      x,
      y,
    ]
  );

  // Whenever we show inline comment, make sure to update toolbar position
  // so that it's not displayed offscreen (eg if comment is near bottom of page)
  useLayoutEffect(() => {
    if (!activeComment) return;

    requestAnimationFrame(() => {
      update();
    });
  }, [activeComment, update]);

  useEffect(() => {
    if (!activeComment || !isOwningEditor) {
      return;
    }

    if (shouldPreserveEditorSelectionForActiveComment({activeComment})) {
      return;
    }

    // One-shot: clear the current Lexical selection so the first reconcile
    // doesn't restore a stale caret.
    clearLiveEditorSelectionForCommentMode(editor);

    // Persistent: block programmatic focus steals (collab updates, editor.update
    // reconciliation) while the comment UI is open.  User-initiated focus
    // (mousedown/keydown on the editor) is still allowed so text selection works.
    return installEditorFocusGuard(editor);
  }, [activeComment, editor, isOwningEditor]);

  /**
   * Finds the top-left cell from a collection of table cells
   */
  const findTopLeftCell = useCallback((tableCells: any[]) => {
    let topLeftCell = tableCells[0];
    let minRow = $getTableRowIndexFromTableCellNode(topLeftCell);
    let minCol = $getTableColumnIndexFromTableCellNode(topLeftCell);

    // Find the cell with the smallest row and column indices
    for (const cell of tableCells) {
      const rowIndex = $getTableRowIndexFromTableCellNode(cell);
      const colIndex = $getTableColumnIndexFromTableCellNode(cell);

      if (rowIndex < minRow || (rowIndex === minRow && colIndex < minCol)) {
        topLeftCell = cell;
        minRow = rowIndex;
        minCol = colIndex;
      }
    }

    return topLeftCell;
  }, []);

  /**
   * Positions the table toolbar at the top-left of the selected cell
   */
  const positionTableToolbar = useCallback(
    (topLeftCell: any) => {
      const cellElement = editor.getElementByKey(topLeftCell.getKey());
      if (cellElement) {
        const cellRect = cellElement.getBoundingClientRect();
        const referenceRect = new DOMRect(cellRect.left + 60, cellRect.top - 60, 1, 1);

        const virtualEl = {
          getBoundingClientRect(): DOMRect {
            return referenceRect;
          },
        };
        refs.setReference(virtualEl);
        setStableFloatingReferenceRect(referenceRect);

        if (update) {
          update();
        }
      }
    },
    [editor, refs, setStableFloatingReferenceRect, update]
  );

  // Update layout rect on resize to properly position toolbar
  useLayoutEffect(() => {
    const updateRect = () => {
      const layoutContainer = document.getElementById('layout-max-width-container');
      if (!layoutContainer) return;
      const rect = layoutContainer.getBoundingClientRect();
      setLayoutRect(rect);
    };
    updateRect();

    window.addEventListener('resize', updateRect);
    return () => window.removeEventListener('resize', updateRect);
  }, []);

  const clearCollapsedCaretToolbarSuppression = useCallback(() => {
    if (!suppressCollapsedCaretToolbarRef.current) {
      return;
    }
    suppressCollapsedCaretToolbarRef.current = false;
    collapsedCaretFocusGuardCleanupRef.current?.();
    collapsedCaretFocusGuardCleanupRef.current = null;
  }, []);

  // Track editor focus state to only show toolbar when editor is focused
  useEffect(() => {
    const editorElement = editor.getRootElement();
    if (!editorElement) return;

    const handleFocus = () => {
      clearCollapsedCaretToolbarSuppression();
      setIsEditorFocused(true);
    };
    const handleBlur = (e: FocusEvent) => {
      // Don't blur if focus is moving to the toolbar or comment box
      const relatedTarget = e.relatedTarget as HTMLElement | null;
      const isToolbarClick = relatedTarget?.closest('[data-selection-toolbar]');
      const isCommentBoxClick = relatedTarget?.closest('[data-comment-box]');
      if (!isToolbarClick && !isCommentBoxClick) {
        setIsEditorFocused(false);
      }
    };

    editorElement.addEventListener('focusin', handleFocus);
    editorElement.addEventListener('focusout', handleBlur);

    // Check initial focus state
    setIsEditorFocused(editorElement.contains(document.activeElement));

    return () => {
      editorElement.removeEventListener('focusin', handleFocus);
      editorElement.removeEventListener('focusout', handleBlur);
    };
  }, [clearCollapsedCaretToolbarSuppression, editor]);

  // Compute effective alwaysShow - only show when focused if alwaysShow is enabled
  const effectiveAlwaysShow = Boolean(alwaysShow && isEditorFocused);

  const updateToolbarVisibility = useCallback(() => {
    editor.update(() => {
      const selection = $getSelection();
      const isCollapsedCaretAlwaysShowState =
        $isRangeSelection(selection) &&
        selection.isCollapsed() &&
        shouldHideToolbarForCollapsedCaretScroll({
          alwaysShow: effectiveAlwaysShow,
          hasActiveSuggestion: Boolean(effectiveActiveAISuggestion),
          hasActiveComment: Boolean(activeComment),
          hasTableSelection: Boolean(tableSelection),
          isCollapsedRangeSelection: true,
        });

      if (suppressCollapsedCaretToolbarRef.current && isCollapsedCaretAlwaysShowState) {
        setIsToolbarVisible(false);
        setIsSelectionActive(false);
        return;
      }

      const rootEl = editor.getRootElement();
      const winSel = window.getSelection();
      if (
        rootEl &&
        winSel &&
        winSel.rangeCount > 0 &&
        winSel.toString().trim() !== '' &&
        isInsideNestedEditor(winSel.getRangeAt(0).commonAncestorContainer, rootEl)
      ) {
        setIsToolbarVisible(false);
        setIsSelectionActive(false);
        return;
      }
      // Don't update toolbar position if comment box is open
      // This prevents jumping to new comments during collab updates
      const commentBoxOpen = document.querySelectorAll('[data-comment-box]').length > 0;
      if (commentBoxOpen) {
        if (applyActiveCommentReference('comment-box-open')) {
          return;
        }
        return;
      }

      if ($isTableSelection(selection)) {
        // Handle table selection - show toolbar and let SelectedTextToolbar decide what to display
        setTableSelection(selection);

        // Position toolbar at top-left of the actual top-left selected cell
        try {
          const nodes = selection.getNodes();
          const tableCells = nodes.filter($isTableCellNode);

          if (tableCells.length > 0) {
            const topLeftCell = findTopLeftCell(tableCells);
            positionTableToolbar(topLeftCell);
          }
        } catch (error) {
          console.warn('Error positioning table toolbar:', error);
        }

        setIsToolbarVisible(true);
        return;
      } else {
        // Clear table selection if not a table selection
        setTableSelection(null);
      }

      if (effectiveActiveAISuggestion && suggestionVirtualElement) {
        // For AI suggestions, position relative to the virtual element encompassing all suggestion nodes
        stableSuggestionElement.getBoundingClientRect =
          suggestionVirtualElement.getBoundingClientRect;
        refs.setReference(stableSuggestionElement);
        setStableFloatingReferenceRect(suggestionVirtualElement.getBoundingClientRect());
        if (update) update();
        return;
      } else if (activeComment && commentVirtualElement && isOwningEditor) {
        if (applyActiveCommentReference('active-comment')) {
          return;
        }
      }

      if (!effectiveAlwaysShow && selection && selection.getTextContent().trim() === '') {
        // Don't hide if a click handler just processed a click (e.g., set activeComment)
        if (clickHandlerProcessedRef.current) {
          // Keep toolbar visible for comment/AI suggestion display
        } else {
          setIsToolbarVisible(false);
          setIsSelectionActive(false);
          return;
        }
      }

      if ($isRangeSelection(selection)) {
        const {anchor, focus} = selection;
        const hasTextSelection = selection.getTextContent().trim() !== '';

        setIsSelectionActive(hasTextSelection || commentBoxOpen);

        // If no text selection and not alwaysShow, hide toolbar
        if (!hasTextSelection && !effectiveAlwaysShow) {
          setIsToolbarVisible(false);
          return;
        }

        // Position at the end of selection (or cursor if no text selected)
        const positionNode = hasTextSelection ? focus : anchor;

        let domRange;
        if (hasTextSelection) {
          // For text selections, create a range that spans the entire selection
          domRange = createDOMRange(
            editor,
            anchor.getNode(),
            anchor.offset,
            focus.getNode(),
            focus.offset
          );
        } else {
          // For cursor-only, position at cursor
          domRange = createDOMRange(
            editor,
            positionNode.getNode(),
            positionNode.offset,
            positionNode.getNode(),
            positionNode.offset
          );
        }

        if (domRange) {
          const boundingRect = domRange.getBoundingClientRect();
          // Hide toolbar if bounding rect has zero dimensions (invalid position)
          if (boundingRect.width === 0 && boundingRect.height === 0) {
            setIsToolbarVisible(false);
            setIsSelectionActive(false);
            return;
          }

          if (boundingRect) {
            const rootElement = editor.getRootElement();
            if (
              rootElement &&
              rootElement.contains(domRange.commonAncestorContainer) &&
              !isInsideNestedEditor(domRange.commonAncestorContainer, rootElement)
            ) {
              // Get fresh bounding rect to ensure accurate coordinates
              const freshBoundingRect = domRange.getBoundingClientRect();

              // Check if the bounding rect is still valid
              if (freshBoundingRect.width === 0 && freshBoundingRect.height === 0) {
                setIsToolbarVisible(false);
                setIsSelectionActive(false);
                return;
              }

              const getReferenceRect = () => getFloatingReferenceRectFromRange(domRange);
              const referenceRect = getReferenceRect();
              if (!referenceRect) {
                setIsToolbarVisible(false);
                setIsSelectionActive(false);
                return;
              }

              // Set the reference for Floating UI. Keep the range live so the draft comment
              // follows the selected text as the page scrolls, even after editor focus moves
              // into the comment composer.
              const virtualEl = {
                getBoundingClientRect(): DOMRect {
                  return getReferenceRect() ?? referenceRect;
                },
                contextElement: rootElement,
              };
              refs.setReference(virtualEl);
              setStableFloatingReferenceRect(referenceRect, getReferenceRect);

              // Force Floating UI to recalculate position
              if (update) {
                update();
              }

              setIsToolbarVisible(true);
              return;
            }
          }
        }
      }

      // No valid position found
      if (!effectiveAlwaysShow) {
        setIsToolbarVisible(false);
        setIsSelectionActive(false);
      }
    });
  }, [
    activeComment,
    applyActiveCommentReference,
    effectiveAlwaysShow,
    effectiveActiveAISuggestion,
    commentVirtualElement,
    editor,
    findTopLeftCell,
    isOwningEditor,
    positionTableToolbar,
    refs,
    setStableFloatingReferenceRect,
    stableSuggestionElement,
    suggestionVirtualElement,
    tableSelection,
    update,
  ]);
  const updateToolbarVisibilityRef = useRef(updateToolbarVisibility);

  useEffect(() => {
    updateToolbarVisibilityRef.current = updateToolbarVisibility;
  }, [updateToolbarVisibility]);

  const hideToolbar = useCallback(
    (e?: MouseEvent) => {
      const mentions = Array.from(document.getElementsByClassName(mentionStyles.container));
      const isMentionClicked = mentions.some((mention) => mention.contains(e?.target as Node));
      const isLinkClicked = (e?.target as HTMLElement)?.closest?.('a');

      // Check if a comment box is currently open (visible in DOM)
      // This prevents closing the comment box when selection is lost due to collaborative edits
      const isCommentBoxOpen = hasOpenCommentBox();

      if (!effectiveAlwaysShow && !isMentionClicked && !isLinkClicked && !isCommentBoxOpen) {
        setIsToolbarVisible(false);
        setIsSelectionActive(false);
      }
    },
    [effectiveAlwaysShow]
  );

  // Check if current global selection is within this editor
  const handleMouseUp = useCallback(
    (e: MouseEvent) => {
      // Reset mouse down state
      setIsMouseDown(false);
      if (mouseUpFrameRef.current !== null) {
        cancelAnimationFrame(mouseUpFrameRef.current);
      }

      mouseUpFrameRef.current = requestAnimationFrame(() => {
        mouseUpFrameRef.current = null;
        const mouseSelectionGesture = mouseSelectionGestureRef.current;
        mouseSelectionGestureRef.current = null;

        const globalSelection = window.getSelection();
        const editorElement = editor.getRootElement();
        const range = globalSelection?.rangeCount ? globalSelection.getRangeAt(0) : null;
        const isSelectionInNestedEditor = Boolean(
          range &&
          editorElement &&
          isInsideNestedEditor(range.commonAncestorContainer, editorElement)
        );
        const isSelectionInEditor = Boolean(
          range &&
          editorElement &&
          editorElement.contains(range.commonAncestorContainer) &&
          !isSelectionInNestedEditor
        );
        const selectedText = isSelectionInEditor ? (globalSelection?.toString().trim() ?? '') : '';

        if (
          mouseSelectionGesture &&
          shouldSuppressMouseUpToolbarRestore({
            hadTextSelectionOnMouseDown: mouseSelectionGesture.hadTextSelectionOnMouseDown,
            didPointerMoveSinceMouseDown: mouseSelectionGesture.didPointerMoveSinceMouseDown,
            clickDetail: e.detail,
          }) &&
          !clickHandlerProcessedRef.current &&
          !pendingClickHandlerRef.current
        ) {
          hideToolbar(e);
          return;
        }

        if (selectedText && activeCommentRef.current) {
          activeCommentRef.current = null;
          setActiveAISuggestion(null);
          if (!setActiveComment(null)) {
            return;
          }
          clickHandlerProcessedRef.current = false;
          setIsToolbarVisible(true);
          setIsSelectionActive(true);
          updateToolbarVisibilityRef.current();
          return;
        }

        if (effectiveAlwaysShow) {
          if (isSelectionInNestedEditor || (pendingClickHandlerRef.current && !selectedText)) {
            return;
          }
          // Wait until after the browser settles the click selection so we don't
          // briefly re-open the toolbar from a stale pre-collapse range.
          updateToolbarVisibility();
          return;
        }

        // if there is no global window selection, hide the toolbar
        if (!globalSelection || globalSelection.rangeCount === 0) {
          hideToolbar(e);
          return;
        }

        // If the selection is not within our editor, hide the toolbar
        if (!isSelectionInEditor) {
          hideToolbar(e);
          return;
        }

        // Only show toolbar if there's actual text selected
        // But don't hide if a click handler just set activeComment/activeAISuggestion
        // (React state is async so we use a ref to track this)
        if (!selectedText && !clickHandlerProcessedRef.current && !pendingClickHandlerRef.current) {
          hideToolbar(e);
          return;
        }

        // Reset the ref for next click
        clickHandlerProcessedRef.current = false;

        // If the selection is within our editor and has text, update the toolbar position
        updateToolbarVisibility();
      });
    },
    [
      editor,
      updateToolbarVisibility,
      effectiveAlwaysShow,
      hideToolbar,
      setActiveAISuggestion,
      setActiveComment,
    ]
  );

  // Toggle toolbar when activeComment changes
  useEffect(() => {
    if (activeComment && commentVirtualElement && isOwningEditor) {
      updateToolbarVisibility();
    }
  }, [activeComment, updateToolbarVisibility, commentVirtualElement, isOwningEditor]);

  useEffect(() => {
    if (!documentSynced || !activeComment?.threadId || !isOwningEditor || commentVirtualElement) {
      return;
    }

    const editorRoot = editor.getRootElement();
    if (!editorRoot) {
      return;
    }

    let rafId: number | null = null;
    const refreshAnchorIfReady = () => {
      if (rafId !== null) {
        return;
      }

      rafId = requestAnimationFrame(() => {
        rafId = null;
        if (createVirtualElementForComment(activeComment.threadId, editorRoot)) {
          setCommentAnchorVersion((version) => version + 1);
        }
      });
    };

    refreshAnchorIfReady();

    const observer = new MutationObserver(refreshAnchorIfReady);
    observer.observe(editorRoot, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-highlight-id'],
    });

    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      observer.disconnect();
    };
  }, [
    activeComment?.threadId,
    commentVirtualElement,
    documentSynced,
    editor,
    isOwningEditor,
    setCommentAnchorVersion,
  ]);

  // Subscribe to selection changes & blur
  useEffect(() => {
    return mergeRegister(
      // Handle Escape key to hide toolbar
      editor.registerCommand(
        KEY_ESCAPE_COMMAND,
        (event: KeyboardEvent) => {
          if (isToolbarVisible && !effectiveAlwaysShow) {
            event.stopPropagation();
            hideToolbar();
            return true;
          }
          return false;
        },
        COMMAND_PRIORITY_HIGH
      ),

      // Use update listener for comprehensive coverage - but only for alwaysShow mode
      ...(effectiveAlwaysShow
        ? [
            editor.registerUpdateListener(({editorState}) => {
              if (pendingClickHandlerRef.current) {
                return;
              }
              editorState.read(() => {
                updateToolbarVisibility();
              });
            }),
          ]
        : // For non-alwaysShow mode, only hide toolbar when selection is lost
          // Showing toolbar is handled by handleMouseUp
          [
            editor.registerUpdateListener(({editorState}) => {
              editorState.read(() => {
                const selection = $getSelection();
                // Only hide toolbar if there's no selection
                if (
                  !selection ||
                  ($isRangeSelection(selection) && selection.getTextContent().trim() === '')
                ) {
                  const rootEl = editor.getRootElement();
                  const winSel = window.getSelection();
                  const nestedHasTextSelection =
                    rootEl &&
                    winSel &&
                    winSel.rangeCount > 0 &&
                    winSel.toString().trim() !== '' &&
                    isInsideNestedEditor(winSel.getRangeAt(0).commonAncestorContainer, rootEl);
                  if (!nestedHasTextSelection) {
                    hideToolbar();
                  }
                }
              });
            }),
          ])
    );
  }, [editor, updateToolbarVisibility, isToolbarVisible, hideToolbar, effectiveAlwaysShow]);

  // Monitor mousedown/click for showing the toolbar and detecting special node clicks
  useEffect(() => {
    const editorElement = editor.getRootElement();
    if (!editorElement) return;

    type NodeClickMatch = {handler: NodeClickHandler; node: LexicalNode};

    const hasTextSelectionInEditor = () => {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0 || selection.toString().trim() === '') {
        return false;
      }

      const range = selection.getRangeAt(0);
      return (
        editorElement.contains(range.commonAncestorContainer) &&
        !isInsideNestedEditor(range.commonAncestorContainer, editorElement)
      );
    };

    const resolveDomCommentClickAction = (target: HTMLElement): (() => void) | null => {
      const highlightElement = target.closest<HTMLElement>('span[data-highlight-id]');
      if (
        !highlightElement ||
        !editorElement.contains(highlightElement) ||
        isInsideNestedEditor(highlightElement, editorElement)
      ) {
        return null;
      }

      // Get the most specific active thread ID (smallest highlighted range among active threads)
      const selectedThreadId = getMostSpecificActiveHighlightId(highlightElement, threads ?? []);
      if (!selectedThreadId) {
        return null;
      }

      return () => {
        if (
          !setActiveComment({
            threadId: selectedThreadId,
            editorKey: editor.getKey(),
            planningCommentOriginId:
              getPlanningCommentOriginIdFromElement(highlightElement) ?? undefined,
          })
        ) {
          return;
        }
        setActiveAISuggestion(null);
        clickHandlerProcessedRef.current = true;
        setIsToolbarVisible(true);
      };
    };

    const resolveNodeClickHandlers = (target: HTMLElement): NodeClickMatch[] => {
      const results: NodeClickMatch[] = [];

      editor.update(
        () => {
          const clickedNode = $getNearestNodeFromDOMNode(target);
          if (!clickedNode) return;

          for (const handler of clickHandlers) {
            let currentNode: LexicalNode | null = clickedNode;
            while (currentNode) {
              const matchedNode: LexicalNode | null = $findMatchingParent(
                currentNode,
                handler.matcher
              );
              if (matchedNode) {
                results.push({handler, node: matchedNode});
                break;
              } else {
                currentNode = null;
              }
            }
          }
        },
        {discrete: true}
      );

      return results;
    };

    const executeHandler = (handler: NodeClickHandler, node: LexicalNode): boolean => {
      let handled = false;

      editor.update(
        () => {
          if (handler.onNodeClick(node, editor)) {
            handled = true;
            clickHandlerProcessedRef.current = true;
          }
        },
        {discrete: true}
      );

      return handled;
    };

    // Detect special node clicks (AI suggestions, comments, redactions, placeholders)
    // Uses capture phase to run before useOutsideClick hooks (which use bubble phase)
    const handleEditorMouseDown = (event: MouseEvent) => {
      clearCollapsedCaretToolbarSuppression();
      const target = event.target as HTMLElement;
      mouseSelectionGestureRef.current = null;

      // Dynamic nodes render embedded UI inside the root editor, including nested read-only
      // Lexical editors for commentable labels. Treat clicks anywhere in that boundary as
      // leaving the root text selection so stale selected-text toolbars do not linger, unless
      // the click is on a comment highlight that should activate its own comment thread.
      if (shouldClearRootSelectionForDynamicNodeMouseDown({editorElement, target})) {
        pendingClickHandlerRef.current = false;
        clickHandlerProcessedRef.current = false;
        // Interactive controls inside dynamic nodes need their click event to fire before an
        // active comment unmounts. CommentDisplay handles that deferred close from outside-click.
        if (shouldCloseActiveCommentForDynamicNodeMouseDown({editorElement, event, target})) {
          if (!setActiveComment(null)) {
            return;
          }
        }
        setActiveAISuggestion(null);
        setTableSelection(null);
        setIsSelectionActive(false);
        setIsToolbarVisible(false);
        setIsMouseDown(false);
        window.getSelection()?.removeAllRanges();
        editor.update(
          () => {
            if ($getSelection() !== null) {
              $setSelection(null);
            }
          },
          {discrete: true, tag: [SKIP_COLLAB_TAG, SKIP_DOM_SELECTION_TAG]}
        );
        return;
      }

      if (isInsideNestedEditor(target, editorElement)) {
        return;
      }

      // Check if clicking on a checkbox list item - let it through to CheckListPlugin
      const checkboxListItem = target.closest('[role="checkbox"]');
      if (checkboxListItem) {
        return;
      }

      if (hasOpenCommentBox()) {
        return;
      }

      const matches = resolveNodeClickHandlers(target);

      if (matches.length > 0 || resolveDomCommentClickAction(target)) {
        pendingClickHandlerRef.current = true;
        return;
      } else {
        const keepToolbarVisibleOnMouseDown = shouldKeepToolbarVisibleOnMouseDown({
          editorElement,
          selection: window.getSelection(),
          target,
          clientX: event.clientX,
          clientY: event.clientY,
        });
        mouseSelectionGestureRef.current = {
          hadTextSelectionOnMouseDown: hasTextSelectionInEditor(),
          keepToolbarVisibleOnMouseDown,
          startX: event.clientX,
          startY: event.clientY,
          didPointerMoveSinceMouseDown: false,
        };
        if (!setActiveComment(null)) {
          return;
        }
        if (!effectiveAlwaysShow && !keepToolbarVisibleOnMouseDown) {
          setIsToolbarVisible(false);
        }
        setIsMouseDown(!keepToolbarVisibleOnMouseDown);
        setActiveAISuggestion(null);
      }
    };

    const handleMouseMove = (event: MouseEvent) => {
      const gesture = mouseSelectionGestureRef.current;
      if (!gesture) {
        return;
      }

      if (
        Math.abs(event.clientX - gesture.startX) >= MOUSE_SELECTION_DRAG_THRESHOLD_PX ||
        Math.abs(event.clientY - gesture.startY) >= MOUSE_SELECTION_DRAG_THRESHOLD_PX
      ) {
        if (!gesture.didPointerMoveSinceMouseDown) {
          gesture.didPointerMoveSinceMouseDown = true;
          if (gesture.keepToolbarVisibleOnMouseDown) {
            if (!effectiveAlwaysShow) {
              setIsToolbarVisible(false);
            }
            setIsMouseDown(true);
          }
        }
      }
    };

    const handleEditorClick = (event: MouseEvent) => {
      clearCollapsedCaretToolbarSuppression();
      pendingClickHandlerRef.current = false;
      const target = event.target as HTMLElement;

      if (isInsideNestedEditor(target, editorElement)) {
        return;
      }

      // Check if clicking on a checkbox list item - let it through to CheckListPlugin
      const checkboxListItem = target.closest('[role="checkbox"]');
      if (checkboxListItem || hasTextSelectionInEditor()) {
        return;
      }

      const domCommentAction = resolveDomCommentClickAction(target);
      if (domCommentAction) {
        domCommentAction();
        return;
      }

      const matches = resolveNodeClickHandlers(target);
      for (const m of matches) {
        if (executeHandler(m.handler, m.node)) {
          setIsToolbarVisible(true);
          return;
        }
      }
    };

    // Handle checkbox clicks specifically since something is blocking the default CheckListPlugin behavior
    const handleCheckboxClick = (event: MouseEvent) => {
      clearCollapsedCaretToolbarSuppression();
      const target = event.target as HTMLElement;
      if (isInsideNestedEditor(target, editorElement)) {
        return;
      }
      const checkboxListItem = target.closest('[role="checkbox"]');

      if (checkboxListItem && editor.isEditable()) {
        const rect = checkboxListItem.getBoundingClientRect();
        const clickX = event.clientX - rect.left;

        if (clickX < 5) {
          event.preventDefault();
          event.stopPropagation();

          editor.update(() => {
            const node = $getNearestNodeFromDOMNode(checkboxListItem);
            if (node && $isListItemNode(node)) {
              const listItemNode = node as ListItemNode;
              const currentChecked = listItemNode.getChecked();
              if (currentChecked !== undefined) {
                listItemNode.setChecked(!currentChecked);
              }
            }
          });
        }
      }
    };

    const handleEditorKeyDown = () => {
      clearCollapsedCaretToolbarSuppression();
    };

    editorElement.addEventListener('mousedown', handleEditorMouseDown, true);
    editorElement.addEventListener('click', handleEditorClick, true);
    editorElement.addEventListener('click', handleCheckboxClick, true);
    editorElement.addEventListener('keydown', handleEditorKeyDown, true);
    document.addEventListener('mousemove', handleMouseMove as EventListener);
    document.addEventListener('mouseup', handleMouseUp as EventListener);

    return () => {
      if (mouseUpFrameRef.current !== null) {
        cancelAnimationFrame(mouseUpFrameRef.current);
      }
      mouseSelectionGestureRef.current = null;
      editorElement.removeEventListener('mousedown', handleEditorMouseDown, true);
      editorElement.removeEventListener('click', handleEditorClick, true);
      editorElement.removeEventListener('click', handleCheckboxClick, true);
      editorElement.removeEventListener('keydown', handleEditorKeyDown, true);
      document.removeEventListener('mousemove', handleMouseMove as EventListener);
      document.removeEventListener('mouseup', handleMouseUp as EventListener);
    };
  }, [
    clearCollapsedCaretToolbarSuppression,
    handleMouseUp,
    effectiveAlwaysShow,
    editor,
    clickHandlers,
    mouseUpFrameRef,
    threads,
    setActiveAISuggestion,
    setActiveComment,
  ]);

  // Monitor scroll and resize to update toolbar position
  useEffect(() => {
    let floatingReferenceFrameId: number | null = null;

    const isScrollInsideCommentBox = (event: Event) => {
      const target = event.target;
      return target instanceof Element && Boolean(target.closest('[data-comment-box]'));
    };

    const scheduleFloatingReferenceUpdate = () => {
      if (!floatingReferenceRectProviderRef.current || floatingReferenceFrameId !== null) {
        return;
      }

      floatingReferenceFrameId = requestAnimationFrame(() => {
        floatingReferenceFrameId = null;
        syncFloatingReferenceRect();
        update();
      });
    };

    const handleScroll = (event: Event) => {
      if (isToolbarVisible) {
        const commentBoxOpen = document.querySelector('[data-comment-box]');
        if (commentBoxOpen) {
          if (!isScrollInsideCommentBox(event)) {
            scheduleFloatingReferenceUpdate();
          }
          return;
        }

        const shouldHideForCollapsedCaret = editor.getEditorState().read(() => {
          const selection = $getSelection();
          return (
            $isRangeSelection(selection) &&
            selection.isCollapsed() &&
            shouldHideToolbarForCollapsedCaretScroll({
              alwaysShow: effectiveAlwaysShow,
              hasActiveSuggestion: Boolean(effectiveActiveAISuggestion),
              hasActiveComment: Boolean(activeComment),
              hasTableSelection: Boolean(tableSelection),
              isCollapsedRangeSelection: true,
            })
          );
        });

        if (shouldHideForCollapsedCaret) {
          suppressCollapsedCaretToolbarRef.current = true;
          clearLiveEditorSelectionForCommentMode(editor);
          const editorRootEl = editor.getRootElement();
          const nativeSelection = window.getSelection();
          if (
            nativeSelection &&
            nativeSelection.rangeCount > 0 &&
            editorRootEl?.contains(nativeSelection.getRangeAt(0).commonAncestorContainer)
          ) {
            nativeSelection.removeAllRanges();
          }
          if (
            editorRootEl &&
            document.activeElement &&
            editorRootEl.contains(document.activeElement)
          ) {
            (document.activeElement as HTMLElement).blur();
          }
          if (!collapsedCaretFocusGuardCleanupRef.current) {
            collapsedCaretFocusGuardCleanupRef.current = installEditorFocusGuard(editor);
          }
          setIsToolbarVisible(false);
          return;
        }

        // Check if the selection is still in the viewport
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          const editorRootEl = editor.getRootElement();
          if (editorRootEl && isInsideNestedEditor(range.commonAncestorContainer, editorRootEl)) {
            setIsToolbarVisible(false);
            return;
          }
          const rect = range.getBoundingClientRect();

          // Only check viewport if we have a valid rect (non-zero position)
          // A zero rect can occur during selection transitions
          const hasValidRect =
            rect.top !== 0 || rect.bottom !== 0 || rect.left !== 0 || rect.right !== 0;

          if (hasValidRect) {
            // Hide toolbar if selection is scrolled out of viewport
            const isInViewport =
              rect.bottom > 0 &&
              rect.top < window.innerHeight &&
              rect.right > 0 &&
              rect.left < window.innerWidth;

            if (!isInViewport) {
              setIsToolbarVisible(false);
            }
          }
        }
      }
    };

    const handleResize = () => {
      if (isToolbarVisible) {
        scheduleFloatingReferenceUpdate();
        updateToolbarVisibility();
      }
    };

    // Listen to scroll events on all elements (capture phase)
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);

    return () => {
      if (floatingReferenceFrameId !== null) {
        cancelAnimationFrame(floatingReferenceFrameId);
      }
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [
    activeComment,
    editor,
    effectiveAlwaysShow,
    effectiveActiveAISuggestion,
    isToolbarVisible,
    syncFloatingReferenceRect,
    tableSelection,
    update,
    updateToolbarVisibility,
  ]);

  const shouldShowCommentToolbar = Boolean(
    activeComment && isOwningEditor && !suppressActiveCommentDisplay
  );
  const showInline = isSelectionActive || effectiveActiveAISuggestion || shouldShowCommentToolbar;
  const hasValidTableSelection = tableSelection && isToolbarVisible;
  const activeCommentCurrentPosition =
    activeCommentPosition?.threadId === activeComment?.threadId ? activeCommentPosition : null;

  useLayoutEffect(() => {
    activeCommentPositionRequestRef.current += 1;
    setActiveCommentReferenceThreadId(null);
    setActiveCommentPosition(null);
    clearActiveCommentAnchorRetry();
  }, [activeComment, clearActiveCommentAnchorRetry, isOwningEditor]);

  useEffect(() => {
    if (
      !shouldShowCommentToolbar ||
      !activeComment?.threadId ||
      activeCommentReferenceThreadId !== activeComment.threadId ||
      !floatingElement
    ) {
      return;
    }

    const frameId = requestAnimationFrame(() => {
      computeActiveCommentPosition('floating-element-ready', activeComment.threadId);
    });

    return () => cancelAnimationFrame(frameId);
  }, [
    activeComment?.threadId,
    activeCommentReferenceThreadId,
    computeActiveCommentPosition,
    floatingElement,
    shouldShowCommentToolbar,
  ]);

  const isWaitingForActiveCommentPosition =
    shouldShowCommentToolbar && !activeCommentCurrentPosition;
  const activeCommentInputAutoFocus = shouldAutoFocusActiveCommentInput({
    activeComment,
    isWaitingForActiveCommentPosition,
    useSourceAwareFocus: Boolean(planningId),
  });
  const hasActiveCommentPosition = Boolean(activeCommentCurrentPosition);

  useEffect(() => {
    if (!shouldShowCommentToolbar || !activeComment?.threadId || !hasActiveCommentPosition) {
      return;
    }

    let frameId: number | null = null;
    const schedulePositionUpdate = (source: string) => {
      if (frameId !== null) {
        return;
      }

      frameId = requestAnimationFrame(() => {
        frameId = null;
        computeActiveCommentPosition(source, activeComment.threadId);
      });
    };

    const handleScroll = () => schedulePositionUpdate('active-comment-scroll');
    const handleResize = () => schedulePositionUpdate('active-comment-resize');

    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);

    return () => {
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [
    activeComment?.threadId,
    computeActiveCommentPosition,
    hasActiveCommentPosition,
    shouldShowCommentToolbar,
  ]);

  const shouldRenderFloatingShell = shouldRenderFloatingCommentShell({
    activeComment,
    isOwningEditor,
    commentVirtualElement,
  });
  // When a comment thread is active, only the owning editor should render the shared comment UI.
  const shouldShowToolbar =
    effectiveActiveAISuggestion || shouldShowCommentToolbar || (!activeComment && isToolbarVisible);

  useEffect(() => {
    if (!activeComment?.threadId) {
      return;
    }

    debugCommentPosition('state', {
      threadId: activeComment.threadId,
      editorKey: editor.getKey(),
      isOwningEditor,
      hasAnchor: Boolean(commentVirtualElement),
      activeCommentReferenceThreadId,
      activeCommentPosition: activeCommentCurrentPosition,
      shouldRenderFloatingShell,
      shouldShowToolbar,
      isWaitingForActiveCommentPosition,
      floating: {x, y, isPositioned},
    });
  }, [
    activeComment?.threadId,
    activeCommentReferenceThreadId,
    activeCommentCurrentPosition,
    commentVirtualElement,
    editor,
    isOwningEditor,
    isPositioned,
    isWaitingForActiveCommentPosition,
    shouldRenderFloatingShell,
    shouldShowToolbar,
    x,
    y,
  ]);

  const floatingZIndex = shouldShowCommentToolbar ? 10 : 11;

  return (
    <>
      {documentSynced &&
        shouldRenderFloatingShell &&
        shouldShowToolbar &&
        createPortal(
          <Box
            ref={setFloatingRef}
            position={shouldShowCommentToolbar ? 'absolute' : strategy}
            style={
              !isMouseDown && (showInline || hasValidTableSelection)
                ? {
                    top: shouldShowCommentToolbar
                      ? (activeCommentCurrentPosition?.y ?? 0) + window.scrollY
                      : (y ?? 0),
                    left: shouldShowCommentToolbar
                      ? (activeCommentCurrentPosition?.x ?? 0) + window.scrollX
                      : (x ?? 0),
                    zIndex: floatingZIndex,
                    visibility: isWaitingForActiveCommentPosition ? 'hidden' : undefined,
                    pointerEvents: isWaitingForActiveCommentPosition ? 'none' : undefined,
                  }
                : {
                    bottom: 16,
                    left: layoutRect ? layoutRect.left + layoutRect.width / 2 : '50%',
                    transform: 'translateX(-50%)',
                    zIndex: floatingZIndex,
                  }
            }
          >
            <SelectedTextToolbar
              isSelectionActive={!isMouseDown && isSelectionActive}
              setIsSelectionActive={setIsSelectionActive}
              isMouseDown={isMouseDown}
              controls={controls}
              tableSelection={tableSelection}
              placement={placement}
              activeCommentInputAutoFocus={activeCommentInputAutoFocus}
              floatingReferenceRect={floatingReferenceRect}
              commentMentions={commentMentions}
              disableAISuggestions={disableAISuggestions}
            />
          </Box>,
          document.body
        )}
    </>
  );
};
